<template>
    <v-card min-width="23vw" class="d-flex align-center justify-center import-trades-card" outlined>
        <div style="width: 100% !important;">
            <v-card-title class="text-center">
                <v-list-item>
                    <v-list-item-title><h2>{{ $t('title_ImportTrades') }}</h2></v-list-item-title>
                    <v-spacer/>
                    <v-list-item-action><v-icon class="icon" @click="closeWindow" icon>fa-times</v-icon></v-list-item-action>
                </v-list-item>
            </v-card-title>
            <v-card-text>
                <v-overlay :dark="false" :absolute="true" :value="overlayProcessingImport"><v-progress-circular :size="70" width="5" indeterminate/></v-overlay>
                <v-stepper v-model="currentStep" :alt-labels="true" class="elevation-0">
                    <v-stepper-header class="elevation-0">
                        <v-stepper-step :complete=" currentStep > 1" step="1">{{ $t('steps_ImportTrades.upload') }}</v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step :complete=" currentStep > 2" step="2">{{ $t('steps_ImportTrades.validate') }}</v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step step="3">{{ $t('steps_ImportTrades.confirm') }}</v-stepper-step>
                    </v-stepper-header>

                    <v-stepper-items class="mt-n5">
                        <v-stepper-content step="1">
                            <v-container fluid>
                                <v-form v-model="valid">
                                    <v-row>
                                        <v-select :label="$t('firstStep.platform')" :placeholder="$t('firstStep.platformHint')"
                                                  :hint="$t('firstStep.platformHint')"
                                                  :items="platforms" @change="changeVideoUrl"
                                                  v-model="platformChoosed" :rules="platformRule" outlined/>
                                    </v-row>
                                    <v-row>
                                        <v-file-input :label="$t('firstStep.file')" prepend-icon=""
                                                      :placeholder="$t('firstStep.fileHint')"
                                                      :hint="$t('firstStep.fileHint')" v-model="file" outlined
                                                      type="file"
                                                      accept=".xlsx,.xls,.csv" :rules="fileRule">
                                            <template v-slot:selection="{ index, text }">
                                                <v-chip v-if="index < 2" color="green darken-3" label small>{{ text }}</v-chip>
                                            </template>
                                        </v-file-input>
                                    </v-row>
                                    <v-row>
                                        <v-autocomplete label="Broker" :placeholder="$t('firstStep.brokerHint')"
                                                        :hint="$t('firstStep.brokerHint')" :items="brokers"
                                                        v-model="brokerToLink" item-text="name" item-value="id"
                                                        :rules="brokerRule" outlined/>
                                    </v-row>
                                    <v-row class="d-flex align-center justify-center text-center mb-1">
                                        <v-btn @click="nextStep(2)">{{ $t('btnNext') }} »</v-btn>
                                    </v-row>
                                    <v-divider v-if="videoUrl !== ''" class="mt-4 mb-3"/>
                                    <v-row v-if="videoUrl !== ''" class="d-flex align-center justify-center text-center">
                                        <p>{{ $t('firstStep.videoPrepend') }} <a :href="videoUrl" target="_blank">{{ $t('firstStep.videoLink') }}</a><br> {{ $t('firstStep.videoAppend') }} {{ platformChoosed }}.</p></v-row>
                                </v-form>
                            </v-container>
                        </v-stepper-content>
                        <v-stepper-content step="2">
                            <v-container fluid>
                                <v-row class="d-flex align-center justify-center">
                                    <p v-html="$t('secondStep.description')"/>
                                </v-row>
                                <v-row>
                                    <v-simple-table height="320px" :dense="true" :fixed-header="true">
                                        <template v-slot:default>
                                            <thead>
                                            <tr>
                                                <th v-for="item in headers" :key="item" class="text-left">{{ item }}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="item in trades" :key="item.sequence">
                                                <td>{{ truncateString(item.brokerAccount.identifier) }}</td>
                                                <td>{{ truncateString(item.assetTicker) }}</td>
                                                <td>{{ truncateString(item.side) }}</td>
                                                <td>{{`${item.openTime.getDate()}/${item.openTime.getMonth()
                                                    + 1}/${item.openTime.getFullYear()}
                                                    ${String(item.openTime.getHours()).padStart(2, '0')}:${String(item.openTime.getMinutes()).padStart(2, '0')}` }}
                                                </td>
                                                <td>{{`${item.closeTime.getDate()}/${item.closeTime.getMonth()
                                                    + 1}/${item.closeTime.getFullYear()}
                                                    ${String(item.closeTime.getHours()).padStart(2, '0')}:${String(item.closeTime.getMinutes()).padStart(2, '0')}` }}
                                                </td>
                                                <td>{{ truncateString(item.openPrice) }}</td>
                                                <td>{{ truncateString(item.closePrice) }}</td>
                                                <td>{{ truncateString(item.netProfit.toFixed(2)) }}</td>
                                                <td>{{ truncateString(item.commissions) }}</td>
                                            </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </v-row>
                                <v-row class="d-flex align-center justify-center text-center mt-5 mb-1">
                                    <v-btn class="mr-3" @click="nextStep(1)">« {{ $t('btnBack_ImportTrades') }}</v-btn>
                                    <v-btn @click="nextStep(3)">{{ $t('btnNext') }} »</v-btn>
                                </v-row>
                            </v-container>
                        </v-stepper-content>
                        <v-stepper-content step="3">
                            <v-container fluid>
                                <v-row class="d-flex align-center justify-center">
                                    <v-img class="mb-5"
                                           src="https://hub.swiset.com/wp-content/uploads/2020/06/Import-Trades.svg"
                                           max-width="91px" contain/>
                                </v-row>
                                <v-row class="d-flex align-center justify-center text-center">
                                    <p>
                                        {{ $t('thirdStep.firstMessage') }} <b>{{ this.trades.length }} trades</b>.<br>
                                        {{ $t('thirdStep.secondMessage') }}<br><br>
                                        <b>- {{ $t('thirdStep.alertMessage') }} -</b>
                                    </p>
                                </v-row>
                                <v-row class="d-flex align-center justify-center text-center mt-5 mb-1">
                                    <v-btn class="mr-3" @click="nextStep(2)">« {{ $t('btnBack_ImportTrades') }}</v-btn>
                                    <v-btn @click="nextStep(4)">{{ $t('btnImport') }}</v-btn>
                                </v-row>
                            </v-container>
                        </v-stepper-content>
                    </v-stepper-items>
                </v-stepper>
            </v-card-text>
        </div>
    </v-card>
</template>

<script>
    import Repository from '../../../repositories/journal';
    import MT4 from '../../../platforms/mt4';
    import MT5 from '../../../platforms/mt5';
    import ATAS from '../../../platforms/atas';
    import XStation from '../../../platforms/xstation';
    import Tradovate from '../../../platforms/tradovate';
    import * as XLSX from 'xlsx';
    import axios from 'axios';

    export default {
        name: "ImportTrades",
        data() {
            return {
                currentStep: 1,
                videoUrl: '',

                // PHASE 0: Data
                phase0: true,
                platforms: [
                    'cTrader',
                    'NinjaTrader',
                    'MT4',
                    'MT5',
                    'ATAS',
                    'XStation',
                    'Tradovate',
                    'TSTrader',
                    //'Thinkorswim'
                ],
                file: null,
                platformChoosed: '',

                // PHASE 1: Data
                fullExcel: null,
                phase1: false,
                trades: [],
                excelTrades: [],
                headers: [
                  this.$t('secondStep.headers.account'),
                  this.$t('secondStep.headers.asset'),
                  this.$t('secondStep.headers.side'),
                  this.$t('secondStep.headers.openTime'),
                  this.$t('secondStep.headers.closeTime'),
                  this.$t('secondStep.headers.openPrice'),
                  this.$t('secondStep.headers.closePrice'),
                  this.$t('secondStep.headers.netProfit'),
                  this.$t('secondStep.headers.commissions'),
                ],

                // PHASE 2: Broker to link
                phase2: false,
                brokers: [],
                brokerToLink: null,
                overlayProcessingImport: false,

                // Rules
                valid: false,
                platformRule: [
                    p => !!p || 'Platform is required',
                ],
                fileRule: [
                    f => !!f || 'File is required',
                ],
                brokerRule: [
                    b => !!b || 'Broker is required',
                ],
            };
        },
        mounted() {
            this.loadBrokers();
        },
        methods: {
            changeVideoUrl() {
                if(this.platformChoosed !== '') {
                    switch (this.platformChoosed) {
                        case 'cTrader':
                            this.videoUrl = 'https://youtu.be/KTaTFGy2l4w';
                        break;
                        case 'NinjaTrader':
                            this.videoUrl = 'https://youtu.be/5IhwQyr7ZI4';
                        break;
                        case 'MT4':
                            this.videoUrl = 'https://youtu.be/04i5jRqIaUM';
                            break;
                        case 'MT5':
                            this.videoUrl = 'https://youtu.be/KhJYf3LzB6c';
                            break;
                        default:
                            this.videoUrl = '';
                        break;
                    }
                }
            },
            async loadBrokers() {
                for(let i = 0; i < 8; i++)
                    await axios.get(`${this.$store.state.backend}/brokers?page=${i}`).then(
                        (response) =>  { response.data.forEach((item) => { this.brokers.push(item); }); } //this.brokers = response.data.map(a => a.name);
                    );
                this.brokers.sort((one, two) => (one.name > two.name)? 1 : (one.name < two.name)? -1 : 0);
            },
            // eslint-disable-next-line no-unused-vars
            async nextStep(phase) {
                // On phase 0
                if(this.currentStep === 1){
                    if (this.valid) {
                        this.importFile(this.file, (this.platformChoosed === 'ATAS')? 1:0);
                        this.currentStep++;
                    } else
                        alert(this.$t('alerts.rulesBroke'));
                }

                // On phase 1
                else if(this.currentStep === 2){
                    this.currentStep = (phase === 1)? 1 : 3;
                    //this.phase0 = phase === 0;
                    //this.phase1 = false;
                    //this.phase2 = phase === 2;
                }

                //On phase 2
                else {
                    if (phase === 4) {
                        if (this.trades.length > 0) {
                            this.overlayProcessingImport = true;
                            await Repository.importTrades(this.trades).then((response) => {
                                if (response.status < 400) {
                                    alert(`${response.data} ${this.$t('alerts.successful')}`);
                                    this.overlayProcessingImport = false;
                                } else {
                                    alert(this.$t('alerts.error'));
                                    this.overlayProcessingImport = false;
                                }
                            });
                        }
                        else
                            alert(this.$t('alerts.zero'));

                        this.closeWindow();
                    } else if (phase === 2) {
                        this.currentStep = 2;
                        //this.phase0 = false;
                        //this.phase1 = true;
                        //this.phase2 = false;
                    }
                }
            },
            importFile(evt, sheetNumber) {
                let f = evt;

                if (f) {
                    let r = new FileReader();

                    r.onload = e => {
                        let contents = this.processExcel(e.target.result);
                        this.fullExcel = JSON.parse(contents);
                        let dataFull = this.fullExcel[Object.keys(this.fullExcel)[sheetNumber]];
                        dataFull.shift();
                        this.excelTrades = dataFull;
                        if(this.platformChoosed === 'MT4')
                            this.trades = MT4.fromExcelToTrades(this.excelTrades, this.brokerToLink);
                        else if(this.platformChoosed === 'MT5')
                            this.trades = MT5.fromExcelToTrades(this.excelTrades, this.brokerToLink);
                        else if(this.platformChoosed === 'ATAS')
                            this.trades = ATAS.fromExcelToTrades(this.excelTrades, this.brokerToLink);
                        else if(this.platformChoosed === 'XStation')
                          this.trades = XStation.fromExcelToTrades(this.excelTrades, this.brokerToLink);
                        else if(this.platformChoosed === 'Tradovate')
                          this.trades = Tradovate.fromExcelToTrades(this.excelTrades, this.brokerToLink);
                        else if(this.platformChoosed === 'TSTrader')
                          this.trades = Tradovate.fromExcelToTrades(this.excelTrades, this.brokerToLink);
                        else
                            this.excelToTrades();
                    };
                    r.readAsBinaryString(f);
                }
            },
            processExcel(data) {
                let workbook = XLSX.read(data, {
                    type: 'binary'
                });

                // eslint-disable-next-line no-unused-vars
                let firstSheet = workbook.SheetNames[0];
                return this.toJson(workbook);
            },
            toJson(workbook) {
                let result = {};
                workbook.SheetNames.forEach(function(sheetName) {
                    let roa = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
                        header: 1,
                        raw: false,
                    });
                    if (roa.length) result[sheetName] = roa;
                });
                return JSON.stringify(result, 2, 2);
            },
            excelToTrades() {
                this.trades = [];
                this.excelTrades.forEach((trade) => {
                    let tradeToAdd = {
                        user: { id: this.$store.state.user.id, login: this.$store.state.user.username },
                        brokerAccount: { identifier: this.matchIndexes(trade, 'account'), broker: { id: this.brokerToLink }, },
                        assetTicker: this.matchIndexes(trade, 'asset'),
                        side: this.matchIndexes(trade, 'side'),
                        openTime: this.convertDate(this.matchIndexes(trade, 'openTime')),
                        closeTime: this.convertDate(this.matchIndexes(trade, 'closeTime')),
                        openPrice: Number(this.removeCurrency(this.matchIndexes(trade, 'openPrice'))),
                        closePrice: Number(this.removeCurrency(this.matchIndexes(trade, 'closePrice'))),
                        netProfit: Number(this.removeCurrency(this.matchIndexes(trade, 'grossProfit'))),
                        commissions: Number(this.removeCurrency(this.matchIndexes(trade, 'commissions'))),
                        pipValue: 0,
                        stopLossPrice: 0,
                    };

                    let priceDistance = Math.abs(tradeToAdd.openPrice - tradeToAdd.closePrice);
                    tradeToAdd.pipValue = (priceDistance > 0.0)? (Math.abs(tradeToAdd.netProfit) / (priceDistance)).toFixed(2) : 0;
                    tradeToAdd.netProfit = tradeToAdd.netProfit - Math.abs(tradeToAdd.commissions);
                    tradeToAdd.stopLossPrice = (tradeToAdd.side === 'BUY')? tradeToAdd.openPrice - priceDistance : tradeToAdd.openPrice + priceDistance;
                    this.trades.push(tradeToAdd);
                });
            },
            matchIndexes(trade, itemType) {
                switch (itemType) {
                    case 'sequence':
                        return trade[0];
                    case 'account':
                        return (this.platformChoosed === 'NinjaTrader')? trade[2] : 'cTrader';
                    case 'asset':
                        return trade[1];
                    case 'side':
                        return (this.platformChoosed === 'NinjaTrader')? (trade[4].startsWith('L')? 'BUY' : 'SELL') :
                            trade[4].startsWith('C')? 'BUY' : trade[4].startsWith('V')? 'SELL' : trade[2].toUpperCase();
                    case 'openTime':
                        return (this.platformChoosed === 'NinjaTrader')? trade[8] : trade[3];
                    case 'closeTime':
                        return (this.platformChoosed === 'NinjaTrader')? trade[9] : trade[4];
                    case 'openPrice':
                        return (this.platformChoosed === 'NinjaTrader')? trade[6] : trade[5];
                    case 'closePrice':
                        return (this.platformChoosed === 'NinjaTrader')? trade[7] : trade[6];
                    case 'grossProfit':
                        return (this.platformChoosed === 'NinjaTrader')? trade[12] : trade[8];
                    case 'commissions':
                        return (this.platformChoosed === 'NinjaTrader')? trade[14] : trade[7];
                }
            },
            convertDate(stringDate) {
                let array = stringDate.split(' ');
                // Date related
                let date = array[0].replace('.', '/').split('/');
                let year = (date[2].length === 2) ? `20${date[2]}`: date[2];
                let month = date[(this.platformChoosed === 'NinjaTrader')? 0 : 1];
                let day = date[(this.platformChoosed === 'NinjaTrader')? 1: 0];

                // Time related
                let time = array[1].split(':');
                let hour = time[0];
                let minute = time[1];
                return new Date(year, month - 1, day, hour, minute);
            },
            removeCurrency(string) {
                let newString = string;
                let isNegativeWithParenthesis = string.includes('(') && string.includes(')') && !string.includes('-');
                let currencies = ['$', '€', '£', '¥', '₽', '₺', 'CHF', 'R', 'S', '₩', '₫', ' ', '(', ')'];
                currencies.forEach((currency) => {newString = newString.replace(currency, '')});
                //newString = (this.platformChoosed === 'NinjaTrader')? newString.replace(',', '') : newString;
                //newString = newString.replace(',', '.');
                newString = this.formatNumber(newString);
                return (isNegativeWithParenthesis)? `-${newString}` : newString;
            },
            formatNumber(string) {
                let number = string;
                if(number.includes('.'))
                    number = number.replace(',', '');
                else
                    number = number.replace(',', '.');
                return number;
            },
            truncateString(string) {
                return (string !== null && string.length > 10)? `${string.substring(0, 10)}...`: string;
            },
            closeWindow() {
                this.$emit('closeImport');
            },
        }
    }
</script>



<style>
.v-application .import-trades-card .primary{
    background-color: var(--v-calmGreen-base) !important;
}
</style>
